import {FC, useCallback, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  CommissionType,
  ConfigureReferralCommissionParams,
  myReferralCode,
} from '../../../../../services/cellcast.v2.types'
import moment from 'moment-timezone'
import {toast} from 'react-toastify'
import {
  useLazyAdminGetAffiliateCountQuery,
  useAdminSetCommissionRateMutation,
} from '../../../../../services/cellcast.v2'
import {Spinner} from 'react-bootstrap'
import ReferralAffiliateList from './ReferralAffiliateList'
import NodataYet from '../../../../Components/NodataYet'
import {ErrorMessage, Field, Form, Formik, FormikHelpers} from 'formik'
import * as Yup from 'yup'
import {ProfileDetailData} from '../../../../Redux/AdminPanel/ProfileDetailsSlice'
import Select from 'react-select'
import ReferralCommissions from './ReferralCommissions'
import ReferralCommissionAmount from './ReferralCommissionAmount'
import {useLocation} from 'react-router-dom'

type ReferralProps = {}
type ConfigureReferralParams = Pick<ConfigureReferralCommissionParams, 'rate' | 'type'>

const CommissionTypeOptions = [
  {
    label: CommissionType.Credit,
    value: CommissionType.Credit,
  },
  {
    label: CommissionType.Money,
    value: CommissionType.Money,
  },
]

function PercentInput({field}: any) {
  return (
    <div className='input-group'>
      <input type='text' {...field} className='form-control' placeholder='Enter value' />
      <div className='input-group-append'>
        <span className='input-group-text'>%</span>
      </div>
    </div>
  )
}

export const Referral: FC<ReferralProps> = (req, res) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const {userProp} = location.state || ({} as any)
  
  const userDataState = useSelector((state: any) => state?.ProfileData?.ProfileData?.user || {})
  const userData = userProp?.user ? userProp.user : userDataState

  const [getAffiliateCount, getAffiliateCountResponse] = useLazyAdminGetAffiliateCountQuery()
  const [setRate, setRateResponse] = useAdminSetCommissionRateMutation()

  // constant variable
  const validationSchema = Yup.object({
    rate: Yup.number().min(0.1).required(),
  })

  const initialValues: ConfigureReferralParams = {
    rate: userData?.commissionPercentage || 10,
    type: userData?.commissionType || CommissionType.Credit,
  }

  const handleCopy = useCallback((code: string) => {
    navigator.clipboard.writeText(code)
    toast.success('Copied!')
  }, [])

  const handleSetRate = useCallback(
    (values: ConfigureReferralParams, helpers: FormikHelpers<ConfigureReferralParams>) => {
      setRate({rate: values.rate, type: values.type, userId: userData._id}).then((res) => {
        if ('data' in res) {
          if (res.data?.error && Object.keys(res.data?.error).length > 0) {
            toast.error(res?.data?.message)
          } else {
            dispatch(ProfileDetailData(userData?._id))
            toast.success(res?.data?.message)
          }
        } else if ('error' in res) {
          const resError = res.error as any
          if (resError.data?.message) {
            toast.error(resError.data?.message || '')
          } else {
            toast.error(res?.error.toString() || '')
          }
        }
      })
    },
    []
  )

  const getStatusCode = (status: string) => {
    switch (status) {
      case myReferralCode.NotSet:
        return <span className='badge badge-light-info'>Not Set</span>
      case myReferralCode.WaitingApproval:
        return <span className='badge badge-light-warning'>Waiting Approval</span>
      case myReferralCode.Active:
        return <span className='badge badge-light-success'>Active</span>
      case myReferralCode.Inactive:
        return <span className='badge badge-light-danger'>Not Active</span>
      case myReferralCode.Rejected:
        return <span className='badge badge-light-danger'>Rejected</span>
    }
  }

  const getResponseAtLabel = (status: string) => {
    switch (status) {
      case myReferralCode.NotSet:
      case myReferralCode.WaitingApproval:
      case myReferralCode.Inactive:
        return <label>Approve/Reject At </label>
      case myReferralCode.Active:
        return <label>Approve At </label>
      case myReferralCode.Rejected:
        return <label>Reject At </label>
    }
  }

  useEffect(() => {
    if (userData && userData._id) {
      getAffiliateCount(userData._id)
    }
  }, [userData])

  return (
    <>
      <div className='card mb-6'>
        <div className='card-body'>
          <div className='d-flex flex-wrap flex-sm-nowrap'>
            <div className='flex-grow-1'>
              <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
                <div className='d-flex flex-column'>
                  <div className='d-flex align-items-center mb-2'>
                    <span className='text-gray-900 text-hover-primary fs-2 fw-bolder me-1'>
                      Account overview
                    </span>
                  </div>
                </div>
                <div className='d-flex my-4'>{/* button action area */}</div>
              </div>

              {userData?.myReferralCodes &&
              Array.isArray(userData?.myReferralCodes) &&
              userData?.myReferralCodes.length > 0 ? (
                <div className='row'>
                  <div className='col-lg-6'>
                    <div className='row'>
                      <div className='col-lg-12 text-center mb-7'>
                        <h4 className='fw-bolder m-0 fs-6'>Referral Code Count</h4>
                      </div>
                      <div
                        className='col-lg-12 mb-5 d-flex justify-content-center align-items-center'
                        style={{minHeight: 100}}
                      >
                        <h1 className='fw-bolder m-0' style={{fontSize: 40}}>
                          {userData?.myReferralCodes.length || 0}
                        </h1>
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-6'>
                    <div className='row'>
                      <div className='col-lg-12 text-center mb-7'>
                        <h4 className='fw-bolder m-0 fs-6'>Affiliate Count</h4>
                      </div>
                      <div
                        className='col-lg-12 mb-5 d-flex justify-content-center align-items-center'
                        style={{minHeight: 100}}
                      >
                        {getAffiliateCountResponse.isLoading ||
                        getAffiliateCountResponse.isFetching ? (
                          <Spinner />
                        ) : (
                          <h1 className='fw-bolder m-0' style={{fontSize: 40}}>
                            {getAffiliateCountResponse.data?.data.total || 0}
                          </h1>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <NodataYet message='No referral code (yet!)' />
              )}
            </div>
          </div>
          {userData?.myReferralCodes &&
            Array.isArray(userData?.myReferralCodes) &&
            userData?.myReferralCodes.length > 0 && (
              <div className='d-flex flex-wrap flex-sm-nowrap mt-5'>
                <div className='flex-grow-1'>
                  <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
                    <div className='d-flex flex-column col-lg-12'>
                      <div className='d-flex align-items-center mb-2'>
                        <h4 className='fw-bolder m-0 fs-6'>Commission Rate</h4>
                      </div>
                      <div className='row'>
                        <div className='col-lg-12'>
                          <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSetRate}
                          >
                            {({isSubmitting, values, setFieldValue}) => (
                              <Form>
                                <div className='row'>
                                  <div className='col-lg-3'>
                                    <div className='form-group'>
                                      <Field
                                        type='text'
                                        id='rate'
                                        name='rate'
                                        className='form-control form-control-flush rounded border-gray-200 border'
                                        placeholder='Enter your commission rate'
                                        component={PercentInput}
                                      />
                                      <ErrorMessage
                                        name='rate'
                                        component='div'
                                        className='text-danger'
                                      />
                                    </div>
                                  </div>
                                  <div className='col-lg-4'>
                                    <Select
                                      value={
                                        CommissionTypeOptions.find(
                                          (el) => el.value === values.type
                                        ) || CommissionTypeOptions[0]
                                      }
                                      onChange={(e: any) => setFieldValue('type', e.value)}
                                      options={CommissionTypeOptions}
                                    />
                                  </div>
                                  <div className='col-lg-5'>
                                    <button className='btn btn-primary' type='submit'>
                                      {setRateResponse.isLoading ? <Spinner /> : 'Configure'}
                                    </button>
                                  </div>
                                </div>
                              </Form>
                            )}
                          </Formik>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>
      {userData?.myReferralCodes &&
        Array.isArray(userData?.myReferralCodes) &&
        userData?.myReferralCodes.length > 0 && (
          <div className='card mb-6'>
            <div className='card-body'>
              <div className='d-flex flex-wrap flex-sm-nowrap'>
                <div className='flex-grow-1'>
                  <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
                    <div className='d-flex flex-column'>
                      <div className='d-flex align-items-center mb-2'>
                        <span className='text-gray-900 text-hover-primary fs-2 fw-bolder me-1'>
                          Referral Code
                        </span>
                      </div>
                    </div>
                    <div className='d-flex justify-content-end'></div>
                  </div>
                </div>
              </div>

              <div className='d-flex flex-wrap flex-sm-nowrap'>
                <div className='flex-grow-1'>
                  <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
                    <div className='d-flex flex-column col-lg-12'>
                      <div className='row'>
                        {userData?.myReferralCodes.map((el: any) => (
                          <div className='col-lg-8 mb-5'>
                            <div className='row'>
                              <div
                                className='col-lg-4 d-flex justify-content-center align-items-center'
                                style={{
                                  fontSize: 20,
                                  backgroundColor: '#f5f8fa',
                                  padding: 10,
                                  paddingLeft: 20,
                                  paddingRight: 20,
                                  borderRadius: 5,
                                  minHeight: 80,
                                }}
                              >
                                <span className='text-truncate'>{el?.code}</span>
                              </div>
                              <div className='col-lg-8 ps-5 d-flex flex-column'>
                                <div className='row mb-2'>
                                  <div className='col-md-6'>
                                    <label>Status </label>
                                  </div>
                                  <div className='col-md-6'>{getStatusCode(el?.status)}</div>
                                </div>
                                <div className='row'>
                                  <div className='col-md-6'>{getResponseAtLabel(el?.status)}</div>
                                  <div className='col-md-6'>
                                    <label className='ms-1'>
                                      {el?.responseAt
                                        ? moment(el?.responseAt).format('DD MMMM YYYY - hh:mm')
                                        : '-'}
                                    </label>
                                  </div>
                                </div>
                                <div className='row'>
                                  <div className='col-md-5 mt-5'>
                                    <button
                                      className='btn btn-sm btn-primary'
                                      onClick={() => handleCopy(el?.code)}
                                    >
                                      <i className='bi bi-clipboard'></i> copy code
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {userData?.myReferralCodes &&
        Array.isArray(userData?.myReferralCodes) &&
        userData?.myReferralCodes.length > 0 && (
          <>
            <ReferralCommissionAmount />
            <ReferralCommissions />
            <ReferralAffiliateList />
          </>
        )}
    </>
  )
}

export default Referral
